import { AfterViewInit, Component, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { TruckOwnerService } from "../truck-owner.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import * as maplibregl from "maplibre-gl";
import { MapGeocoder } from "@angular/google-maps";

declare var $: any;

@Component({
  selector: "app-track",
  templateUrl: "./track.component.html",
  styleUrls: ["./track.component.css"],
})
export class TrackComponent implements AfterViewInit, OnDestroy {
  token: any;
  isLoading: boolean = false;
  isLoadingMap: boolean = false;
  markerPositions: any = [];
  zoom = 4;
  intransit: boolean = false;
  truckStatus: boolean = false;
  infoContent = "";
  resArray: any;
  truckOfflineStatus: boolean = false;
  arrayMarker: any = [];
  statusOffline: string = "OFFLINE";
  filteredMarkers: any[] = [];
  isSelectedTruck: string | null = null;
  searchQuery: string = '';
  highlightedTruckNumber: string | null = null;

  // MapLibre properties
  map: maplibregl.Map | undefined;
  mapMarkers: maplibregl.Marker[] = [];
  currentRoutePoints: any[] = [];
  currentRouteSegments: any[] = [];
  routeIntervalId: any;

  constructor(private services: TruckOwnerService, private http: HttpClient, private geocoder: MapGeocoder) {}

  ngOnInit(): void {
    this.token = sessionStorage.getItem("to_token");
    sessionStorage.setItem("tripType", "track");
  }

  ngAfterViewInit() {
    this.initializeMap();
    this.getTrack();
  }

  ngOnDestroy() {
    this.stopRoutePolling();
    if (this.map) {
      this.map.remove();
    }
  }

  async initializeMap() {
    let styleJson: any;
    try {
      styleJson = await this.http
        .get("/assets/map-styles/maptiler-streets-local.json")
        .toPromise();

      if (styleJson.sources && styleJson.sources.openmaptiles) {
        styleJson.sources.openmaptiles.url = environment.tile_server_url;
      }
    } catch (e) {
      console.error("Failed to load map style (/assets/...).", e);
      styleJson = { version: 8, sources: {}, layers: [] };
    }

    this.map = new maplibregl.Map({
      container: 'map-container',
      style: styleJson,
      center: [78.9629, 20.5937],
      zoom: 5.11,
      minZoom: 5.11,
      maxZoom: 18.06,
      maxBounds: [[67.67457, 5.896067], [97.41989, 35.730908]],
      attributionControl: false,
      transformRequest: (url, resourceType) => {
        if (url.includes(environment.tile_server_url)) {
          return {
            url: url,
            headers: { 'X-API-Key': environment.tile_api_key }
          };
        }
        return { url };
      }
    });

    this.map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
  }

  addMarker(locationInfo: any) {
    let truckLocDetails = this.markerPositions.find((item: any) => {
      return item.truckNumber == locationInfo.truckNumber;
    });

    if (
      !truckLocDetails ||
      (truckLocDetails.location &&
        truckLocDetails.location.lat != locationInfo.location.lat &&
        truckLocDetails.location.lng != locationInfo.location.lng)
    ) {
      this.markerPositions = this.markerPositions.filter(
        (item: any) => item.truckNumber != locationInfo.truckNumber
      );
      this.markerPositions.push(locationInfo);
    }
  }

  getTrack() {
    this.isLoading = true;
    this.services.Track(this.token).subscribe((response: any) => {
      const resArray = response.body.record;
      this.markerPositions = []; 
      for (let i = 0; i < resArray.length; i++) {
        this.addMarker({
          trip_id: resArray[i]?.tripdetails?.trip_id,
          tripdetails: resArray[i]?.tripdetails,
          truckNumber: resArray[i].vehicleNumber,
          mobile: resArray[i].driverDetails?.mobile,
          driverName: resArray[i].driverDetails?.username,
          truckStatus: resArray[i].driverRouteDetails?.driver_route_status,
          startdate: resArray[i].tripdetails?.trip_start,
          address: resArray[i].address,
          updateDate: resArray[i].createdAt,
          location: {
            lat: Number(resArray[i].latitude),
            lng: Number(resArray[i].longitude),
          },
          geofenceRadius: resArray[i].geofenceRadius,
        });
      }
      this.filterTrucks(); 
      this.refreshMaplibreMarkers();
      this.isLoading = false;
    });
  }

  filterTrucks() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.highlightedTruckNumber = null;
      this.isSelectedTruck = null;
      this.removeRouteLayers();
      this.stopRoutePolling();
      this.refreshMaplibreMarkers();
      return;
    }
    const match = this.markerPositions.find(
      (marker: any) =>
        marker.truckNumber?.toLowerCase().includes(query) ||
        marker.driverName?.toLowerCase().includes(query)
    );
    if (match) {
      this.highlightedTruckNumber = match.truckNumber;
      this.selectTruck(match, false);
    } else {
      this.highlightedTruckNumber = null;
    }
  }

  getMarkerIconUrl(truckStatus: string, tripStatus: string): string {
    if (!truckStatus || truckStatus === 'offline' || truckStatus === 'null') {
      return '/assets/img/truck_gray.png';
    }
    if (truckStatus === 'online') {
      if (tripStatus === 'tripstarted' || tripStatus === 'inprogress') {
        return '/assets/img/truck_blue.png';
      }
      return '/assets/img/truck_green.png';
    }
    return '/assets/img/truck_gray.png';
  }

  refreshMaplibreMarkers() {
    if (!this.map) return;
    
    // Create markers if they don't exist yet, or update if they do
    this.markerPositions.forEach((markerInfo: any, index: number) => {
      let existingMarker = this.mapMarkers.find((m: any) => m.truckNumber === markerInfo.truckNumber);
      
      if (existingMarker) {
        if (markerInfo.location && markerInfo.location.lat && markerInfo.location.lng) {
          existingMarker.setLngLat([markerInfo.location.lng, markerInfo.location.lat]);
          const el = existingMarker.getElement();
          el.style.backgroundImage = `url(${this.getMarkerIconUrl(markerInfo.truckStatus, markerInfo.tripdetails?.trip_status)})`;
        }
      } else if (!existingMarker && markerInfo.location && markerInfo.location.lat && markerInfo.location.lng) {
        const el = document.createElement('div');
        el.className = 'maplibre-marker';
        el.style.width = '32px';
        el.style.height = '32px';
        el.style.backgroundImage = `url(${this.getMarkerIconUrl(markerInfo.truckStatus, markerInfo.tripdetails?.trip_status)})`;
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundPosition = 'center';
        el.style.cursor = 'pointer';
        
        el.addEventListener('click', () => {
          this.marker(this.markerPositions, index);
        });

        const marker = new maplibregl.Marker({element: el})
          .setLngLat([markerInfo.location.lng, markerInfo.location.lat])
          .addTo(this.map!);
          
        (marker as any).truckNumber = markerInfo.truckNumber;
        (marker as any).htmlElement = el;
        this.mapMarkers.push(marker);
      }
    });

    // Toggle visibility of existing markers based on selection
    this.mapMarkers.forEach((m: any) => {
      if (this.isSelectedTruck && m.truckNumber !== this.isSelectedTruck) {
        m.htmlElement.style.display = 'none';
      } else {
        m.htmlElement.style.display = 'block';
      }
    });
  }

  selectTruck(truck: any, fromMap: boolean) {
    this.isSelectedTruck = truck.truckNumber;
    if (!truck.location) return;

    if (this.map) {
      this.map.flyTo({
        center: [truck.location.lng, truck.location.lat],
        zoom: 13,
        speed: 1.2
      });
    }

    this.removeRouteLayers();
    this.stopRoutePolling();

    const tripId = truck.tripdetails?._id;
    if (tripId) {
      this.loadTripRouteHistory(tripId, truck.truckNumber);
      this.startRoutePolling(tripId, truck.truckNumber);
    }
    
    // Refresh markers so only the selected one remains visible
    this.refreshMaplibreMarkers();
  }

  closeModel() {
    // modal is handled by data-dismiss in HTML, but we force hide it here
    $('#myModal').modal('hide');
    this.isSelectedTruck = null;
    this.removeRouteLayers();
    this.stopRoutePolling();
    this.refreshMaplibreMarkers();
  }

  marker(item: any[], i: number): void {
    this.arrayMarker = item[i];
    this.selectTruck(item[i], true);
    
    if (
      this.arrayMarker?.truckStatus === "null" ||
      this.arrayMarker?.truckStatus === undefined ||
      this.arrayMarker?.truckStatus === ""
    ) {
      this.truckOfflineStatus = true;
      this.intransit = false;
      this.truckStatus = false;
    } else if (this.arrayMarker?.truckStatus === "offline") {
      this.intransit = false;
      this.truckStatus = true;
      this.truckOfflineStatus = false;
    } else if (this.arrayMarker?.truckStatus === "online") {
      this.intransit = false;
      this.truckStatus = true;
      this.truckOfflineStatus = false;
    }
    
    // Open modal programmatically since we removed the google maps window
    $('#myModal').modal('show');

    if ((!this.arrayMarker.address || String(this.arrayMarker.address).toLowerCase() === 'unknown') && this.arrayMarker.location?.lat && this.arrayMarker.location?.lng) {
      this.geocoder
        .geocode({
          location: {
            lat: Number(this.arrayMarker.location.lat),
            lng: Number(this.arrayMarker.location.lng),
          },
        })
        .subscribe(({ results }: any) => {
          if (results && results.length) {
            this.arrayMarker.address = results[0].formatted_address;
          }
        });
    }
  }

  // --- Route Tracking Methods ---
  loadTripRouteHistory(tripId: string, vNo: string) {
    if (!tripId) return;

    this.services.tracking(this.token, tripId).subscribe(
      (response: any) => {
        this.isLoadingMap = false;
        const json = response.body || response;
        const dataObj = json.data || json;
        const rawPoints = (dataObj.truckLocInfo || dataObj.truckLocation || []).filter((p: any) => (p.lat && p.lng) || (p.latitude && p.longitude));

        if (rawPoints.length === 0) {
          console.warn("No route points found for trip ID:", tripId);
          return;
        }

        const mappedPoints = rawPoints.map((p: any) => ({
           ...p,
           latitude: p.lat || p.latitude,
           longitude: p.lng || p.longitude,
           trackDateTime: p.createdAt || p.trackDateTime
        }));

        mappedPoints.sort((a: any, b: any) => new Date(a.trackDateTime).getTime() - new Date(b.trackDateTime).getTime());

        this.currentRoutePoints = mappedPoints;
        this.currentRouteSegments = dataObj.routeSegments || [];
        this.processAndRenderRoute(vNo, true);
      },
      (error: any) => {
        this.isLoadingMap = false;
        console.error("Failed to query location history for route trace:", error);
      }
    );
  }

  startRoutePolling(tripId: string, vNo: string) {
    if (this.routeIntervalId) {
      clearInterval(this.routeIntervalId);
    }
    this.routeIntervalId = setInterval(() => {
      this.pollTripRouteHistory(tripId, vNo);
    }, 10000);
  }

  stopRoutePolling() {
    if (this.routeIntervalId) {
      clearInterval(this.routeIntervalId);
      this.routeIntervalId = null;
    }
  }

  pollTripRouteHistory(tripId: string, vNo: string) {
    if (!tripId) return;

    this.services.tracking(this.token, tripId).subscribe(
      (response: any) => {
        const json = response.body || response;
        const dataObj = json.data || json;
        const rawPoints = (dataObj.truckLocInfo || dataObj.truckLocation || []).filter((p: any) => (p.lat && p.lng) || (p.latitude && p.longitude));

        if (rawPoints.length === 0) return;

        const mappedPoints = rawPoints.map((p: any) => ({
           ...p,
           latitude: p.lat || p.latitude,
           longitude: p.lng || p.longitude,
           trackDateTime: p.createdAt || p.trackDateTime
        }));

        mappedPoints.sort((a: any, b: any) => new Date(a.trackDateTime).getTime() - new Date(b.trackDateTime).getTime());

        const lastPointTime = this.currentRoutePoints.length > 0 
          ? new Date(this.currentRoutePoints[this.currentRoutePoints.length - 1].trackDateTime).getTime() 
          : 0;

        const brandNewPoints = mappedPoints.filter((p: any) => new Date(p.trackDateTime).getTime() > lastPointTime);

        if (brandNewPoints.length > 0) {
          this.currentRoutePoints = [...this.currentRoutePoints, ...brandNewPoints];
          this.currentRouteSegments = dataObj.routeSegments || [];
          
          const latestPoint = this.currentRoutePoints[this.currentRoutePoints.length - 1];
          const markerIdx = this.markerPositions.findIndex((m: any) => m.truckNumber === vNo);
          if (markerIdx !== -1) {
            this.markerPositions[markerIdx].location = {
              lat: Number(latestPoint.latitude),
              lng: Number(latestPoint.longitude)
            };
            this.markerPositions[markerIdx].updateDate = latestPoint.trackDateTime;
          }
          this.refreshMaplibreMarkers();
          this.processAndRenderRoute(vNo, false);
        }
      },
      (error: any) => {
        console.error("Failed to poll route trace:", error);
      }
    );
  }

  processAndRenderRoute(vNo: string, fitBounds: boolean = false) {
    if (!this.map) return;
    const segments = this.currentRouteSegments;
    const historyPoints = this.currentRoutePoints;

    let routeCoordinates: number[][] = [];
    if (segments && segments.length > 0) {
      segments.forEach((segment: any) => {
        if (segment.routePolyline && segment.routePolyline.coordinates) {
          const coords = segment.routePolyline.coordinates;
          routeCoordinates = routeCoordinates.concat(coords);
        } else if (Array.isArray(segment)) {
          // Check if segment is directly an array of [lng, lat]
          if (segment.length > 0 && Array.isArray(segment[0])) {
            routeCoordinates = routeCoordinates.concat(segment);
          }
        }
      });
    } else {
      routeCoordinates = historyPoints.map((p: any) => [parseFloat(p.longitude), parseFloat(p.latitude)]);
    }

    if (routeCoordinates.length === 0) return;

    const source = this.map.getSource("route") as maplibregl.GeoJSONSource;
    if (source) {
      source.setData({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: routeCoordinates,
        },
      });
    } else {
      this.map.addSource("route", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: routeCoordinates,
          },
        },
      });

      this.map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#f58634",
          "line-width": 5,
          "line-opacity": 0.8,
        },
      });
    }

    if (fitBounds) {
      const bounds = new maplibregl.LngLatBounds(
        routeCoordinates[0] as [number, number],
        routeCoordinates[0] as [number, number]
      );
      for (const coord of routeCoordinates) {
        bounds.extend(coord as [number, number]);
      }
      this.map.fitBounds(bounds, { padding: 50, maxZoom: 14 });
    }
  }

  removeRouteLayers() {
    if (!this.map) return;
    if (this.map.getLayer("route-line")) {
      this.map.removeLayer("route-line");
    }
    if (this.map.getSource("route")) {
      this.map.removeSource("route");
    }
  }
}
